import React, {useEffect} from "react";
import * as ReactBootstrap from "react-bootstrap";
import {Controller, useForm} from "react-hook-form";
import type {Control} from "react-hook-form";

import {
  EMI_OR_BUYBACK_OPTIONS,
  EMPTY_BUYBACK_FORM_VALUES,
  PATH_OPTIONS,
  REJECTION_REASON_OPTIONS,
  buybackFormValuesToPayload,
  buybackRecordToFormValues,
} from "./types";
import type {
  BuybackEMIFormValues,
  BuybackEMIRecord,
  SaveBuybackEMIRecordPayload,
} from "./types";

/** Cascade reset map: keys downstream of each upstream field. */
const DEPENDENT_FIELDS: Record<
  "eligibilityYesNo" | "path" | "emiOrBuyback" | "buybackOpted",
  ReadonlyArray<keyof BuybackEMIFormValues>
> = {
  eligibilityYesNo: [
    "path",
    "emiOrBuyback",
    "buybackOpted",
    "quotationAmount",
    "quotationId",
    "rejectionReason",
  ],
  path: [
    "emiOrBuyback",
    "buybackOpted",
    "quotationAmount",
    "quotationId",
    "rejectionReason",
  ],
  emiOrBuyback: [
    "buybackOpted",
    "quotationAmount",
    "quotationId",
    "rejectionReason",
  ],
  buybackOpted: ["quotationAmount", "quotationId", "rejectionReason"],
 
};

interface SelectFieldProps {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  children: React.ReactNode;
  error?: string;
}

/** Bootstrap-form-select wrapped to mirror the Controller render shape. */
function SelectField({
  id,
  label,
  value,
  placeholder,
  onChange,
  onBlur,
  children,
  error,
}: SelectFieldProps): JSX.Element {
  return (
    <ReactBootstrap.Form.Group controlId={id} className="mb-0">
      <ReactBootstrap.Form.Label>{label}</ReactBootstrap.Form.Label>
      <ReactBootstrap.Form.Select
        value={value}
        onChange={(event) =>
          onChange((event.target as HTMLSelectElement).value)
        }
        onBlur={onBlur}
        isInvalid={Boolean(error)}
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {children}
      </ReactBootstrap.Form.Select>
      {error ? (
        <ReactBootstrap.Form.Control.Feedback type="invalid">
          {error}
        </ReactBootstrap.Form.Control.Feedback>
      ) : null}
    </ReactBootstrap.Form.Group>
  );
}

/**
 * Quotation ID capture — shown on both buyback branches (opted yes/no), so it
 * lives in one component to keep the two render sites in sync.
 */
function QuotationIdField({
  control,
  error,
  required = true,
}: {
  control: Control<BuybackEMIFormValues>;
  error?: string;
  required?: boolean;
}): JSX.Element {
  return (
    <Controller
      name="quotationId"
      control={control}
      rules={required ? {required: "Required"} : {}}
      render={({field}) => (
        <ReactBootstrap.Form.Group controlId="bb-quote-id" className="mb-0 mt-3">
          <ReactBootstrap.Form.Label>Quotation ID</ReactBootstrap.Form.Label>
          <ReactBootstrap.Form.Control
            type="text"
            placeholder="Enter quotation ID"
            value={field.value}
            onChange={(e) => field.onChange(e.target.value)}
            onBlur={field.onBlur}
            isInvalid={Boolean(error)}
          />
          <ReactBootstrap.Form.Control.Feedback type="invalid">
            {error}
          </ReactBootstrap.Form.Control.Feedback>
        </ReactBootstrap.Form.Group>
      )}
    />
  );
}

/** Single step rendered as a dark-headered card to match sibling tabs. */
function StepCard({
  title,
  children,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <ReactBootstrap.Card className="mb-3">
      <ReactBootstrap.Card.Header className="bg-dark text-white fw-semibold">
        {title}
      </ReactBootstrap.Card.Header>
      <ReactBootstrap.Card.Body>{children}</ReactBootstrap.Card.Body>
    </ReactBootstrap.Card>
  );
}

export interface BuybackEMIFormProps {
  /** Existing record to seed the form with; ``null`` for a fresh capture. */
  record: BuybackEMIRecord | null;
  isSaving: boolean;
  isLoading: boolean;
  /** Submit handler; receives the API-shaped payload. */
  onSubmit: (payload: SaveBuybackEMIRecordPayload) => Promise<unknown>;
  /** Optional cancel/back action — only shown when defined. */
  onCancel?: () => void;
}

/**
 * BuybackEMIForm
 *
 * Step-based conditional form following the Buyback / EMI flow. Owned by
 * :func:`BuybackTab`; the parent decides when to render this form vs the
 * read-only summary based on whether a record already exists.
 */
export const BuybackEMIForm: React.FC<BuybackEMIFormProps> = ({
  record,
  isSaving,
  isLoading,
  onSubmit,
  onCancel,
}) => {
  const {
    control,
    watch,
    setValue,
    handleSubmit,
    reset,
    formState: {errors},
  } = useForm<BuybackEMIFormValues>({
    defaultValues: EMPTY_BUYBACK_FORM_VALUES,
  });

  // Hydrate the form whenever the loaded record changes (e.g. tab opened or
  // record id flips after the first save). Keying on id|null avoids
  // re-resetting when the same record re-renders.
  useEffect(() => {
    reset(buybackRecordToFormValues(record));
  }, [record?.id, reset, record]);

  const eligibilityYesNo = watch("eligibilityYesNo");
  const path = watch("path");
  const emiOrBuyback = watch("emiOrBuyback");
  const buybackOpted = watch("buybackOpted");

  // Cascade reset — clearing or changing an upstream selection always wipes
  // dependents to keep form state coherent and validation honest.
  const resetDependentsAfter = (
    field: keyof typeof DEPENDENT_FIELDS,
  ): void => {
    for (const dep of DEPENDENT_FIELDS[field]) {
      setValue(dep, "" as never, {shouldDirty: true});
    }
  };

  const handleFormSubmit = async (
    values: BuybackEMIFormValues,
  ): Promise<void> => {
    await onSubmit(buybackFormValuesToPayload(values));
  };

  const isBusy = isLoading || isSaving;
  const submitLabel = isSaving
    ? "Saving…"
    : record?.id != null
      ? "Update"
      : "Save";
  const resetLabel = record?.id != null ? "Revert" : "Reset";

  return (
    <ReactBootstrap.Form
      noValidate
      onSubmit={handleSubmit(handleFormSubmit)}
      className="p-2"
    >
      {/* Q1 — eligibility yes/no */}
      <StepCard title="Step 1: Eligibility Check">
        <Controller
          name="eligibilityYesNo"
          control={control}
          rules={{required: "Required"}}
          render={({field}) => (
            <SelectField
              id="bb-eligibility"
              label="Did customer eligible for No Cost EMI or Buyback if he didn't want to proceed for any Hardware Service options or agree for No Cost EMI / Buyback options?"
              placeholder="Select Yes / No"
              value={field.value}
              onBlur={field.onBlur}
              onChange={(value) => {
                field.onChange(value);
                resetDependentsAfter("eligibilityYesNo");
              }}
              error={errors.eligibilityYesNo?.message}
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </SelectField>
          )}
        />
      </StepCard>

      {/* Q1 = yes → choose Hardware Services vs No Cost EMI / Buyback */}
      {eligibilityYesNo === "yes" && (
        <StepCard title="Step 2: Hardware Services or EMI / Buyback?">
          <Controller
            name="path"
            control={control}
            rules={{required: "Required"}}
            render={({field}) => (
              <SelectField
                id="bb-path"
                label="Select one option"
                placeholder="Select an option"
                value={field.value}
                onBlur={field.onBlur}
                onChange={(value) => {
                  field.onChange(value);
                  resetDependentsAfter("path");
                }}
                error={errors.path?.message}
              >
                {PATH_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </SelectField>
            )}
          />
        </StepCard>
      )}

      {/* path = emi-buyback → Q3 EMI vs Buyback */}
      {path === "emi-buyback" && (
        <StepCard title="Step 3: EMI / Buyback Selection">
          <Controller
            name="emiOrBuyback"
            control={control}
            rules={{required: "Required"}}
            render={({field}) => (
              <SelectField
                id="bb-emi-or-buyback"
                label="Did Customer eligible for No Cost EMI if Customer not agree for full payment?"
                placeholder="Select EMI / Buyback Option"
                value={field.value}
                onBlur={field.onBlur}
                onChange={(value) => {
                  field.onChange(value);
                  resetDependentsAfter("emiOrBuyback");
                }}
                error={errors.emiOrBuyback?.message}
              >
                {EMI_OR_BUYBACK_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </SelectField>
            )}
          />
        </StepCard>
      )}

      {/* emiOrBuyback = buyback → Q4 buyback opted yes/no */}
      {emiOrBuyback === "buyback" && (
        <StepCard title="Step 4: Buyback Confirmation">
          <Controller
            name="buybackOpted"
            control={control}
            rules={{required: "Required"}}
            render={({field}) => (
              <SelectField
                id="bb-opted"
                label="Did Customer opt for BuyBack?"
                placeholder="Select Yes / No"
                value={field.value}
                onBlur={field.onBlur}
                onChange={(value) => {
                  field.onChange(value);
                  resetDependentsAfter("buybackOpted");
                }}
                error={errors.buybackOpted?.message}
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </SelectField>
            )}
          />
        </StepCard>
      )}

      {/* buybackOpted = yes → quotation amount */}
      {buybackOpted === "yes" && (
        <StepCard title="Step 5: Mention Quotation Amount">
          <Controller
            name="quotationAmount"
            control={control}
            rules={{
              required: "Required",
              validate: (value) =>
                value === "" ||
                !Number.isNaN(Number(value)) ||
                "Must be a number",
            }}
            render={({field}) => (
              <ReactBootstrap.Form.Group controlId="bb-quote" className="mb-0">
                <ReactBootstrap.Form.Label>
                  Quotation Amount
                </ReactBootstrap.Form.Label>
                <ReactBootstrap.InputGroup>
                  <ReactBootstrap.InputGroup.Text>
                    ₹
                  </ReactBootstrap.InputGroup.Text>
                  <ReactBootstrap.Form.Control
                    type="number"
                    inputMode="decimal"
                    placeholder="Enter quotation amount"
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    onBlur={field.onBlur}
                    isInvalid={Boolean(errors.quotationAmount?.message)}
                  />
                  <ReactBootstrap.Form.Control.Feedback type="invalid">
                    {errors.quotationAmount?.message}
                  </ReactBootstrap.Form.Control.Feedback>
                </ReactBootstrap.InputGroup>
              </ReactBootstrap.Form.Group>
            )}
          />
          <QuotationIdField control={control} error={errors.quotationId?.message} />
        </StepCard>
      )}

      {/* buybackOpted = no → rejection reason */}
      {buybackOpted === "no" && (
        <StepCard title="Step 5: Why was Buyback rejected?">
          <Controller
            name="rejectionReason"
            control={control}
            rules={{required: "Required"}}
            render={({field}) => (
              <SelectField
                id="bb-reject"
                label="Why?"
                placeholder="Select Rejection Reason"
                value={field.value}
                onBlur={field.onBlur}
                onChange={field.onChange}
                error={errors.rejectionReason?.message}
              >
                {REJECTION_REASON_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </SelectField>
            )}
          />
           <Controller
            name="quotationAmount"
            control={control}
            rules={{
              required: "Required",
              validate: (value) =>
                value === "" ||
                !Number.isNaN(Number(value)) ||
                "Must be a number",
            }}
            render={({field}) => (
              <ReactBootstrap.Form.Group controlId="bb-quote" className="mb-0">
                <ReactBootstrap.Form.Label>
                  Quotation Amount
                </ReactBootstrap.Form.Label>
                <ReactBootstrap.InputGroup>
                  <ReactBootstrap.InputGroup.Text>
                    ₹
                  </ReactBootstrap.InputGroup.Text>
                  <ReactBootstrap.Form.Control
                    type="number"
                    inputMode="decimal"
                    placeholder="Enter quotation amount"
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    onBlur={field.onBlur}
                    isInvalid={Boolean(errors.quotationAmount?.message)}
                  />
                  <ReactBootstrap.Form.Control.Feedback type="invalid">
                    {errors.quotationAmount?.message}
                  </ReactBootstrap.Form.Control.Feedback>
                </ReactBootstrap.InputGroup>
              </ReactBootstrap.Form.Group>
            )}
          />
          <QuotationIdField
            control={control}
            error={errors.quotationId?.message}
            required={false}
          />
        </StepCard>
      )}

      {/* Final step — free-text remarks (always shown, required) */}
      <StepCard title="Remarks">
        <Controller
          name="remarks"
          control={control}
          rules={{
            validate: (value) =>
              value.trim() !== "" || "Required",
          }}
          render={({field}) => (
            <ReactBootstrap.Form.Group controlId="bb-remarks" className="mb-0">
              <ReactBootstrap.Form.Label>
                Remarks or context
              </ReactBootstrap.Form.Label>
              <ReactBootstrap.Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter remarks…"
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={field.onBlur}
                isInvalid={Boolean(errors.remarks?.message)}
              />
              <ReactBootstrap.Form.Control.Feedback type="invalid">
                {errors.remarks?.message}
              </ReactBootstrap.Form.Control.Feedback>
            </ReactBootstrap.Form.Group>
          )}
        />
      </StepCard>

      <div className="d-flex flex-wrap align-items-center gap-2 pt-2">
        <ReactBootstrap.Button type="submit" variant="dark" disabled={isBusy}>
          {isSaving ? (
            <>
              <ReactBootstrap.Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              {submitLabel}
            </>
          ) : (
            submitLabel
          )}
        </ReactBootstrap.Button>
        <ReactBootstrap.Button
          type="button"
          variant="outline-dark"
          disabled={isBusy}
          onClick={() => reset(buybackRecordToFormValues(record))}
        >
          {resetLabel}
        </ReactBootstrap.Button>
        {onCancel ? (
          <ReactBootstrap.Button
            type="button"
            variant="link"
            disabled={isBusy}
            onClick={onCancel}
          >
            Cancel
          </ReactBootstrap.Button>
        ) : null}
        {isLoading ? (
          <span className="text-muted small ms-2">
            <ReactBootstrap.Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
              className="me-2"
            />
            Loading existing record…
          </span>
        ) : null}
      </div>
    </ReactBootstrap.Form>
  );
};

export default BuybackEMIForm;
